import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { getMerchantCustomers, getMerchantCustomer, addCustomerNote } from '../../lib/api';
import { toast } from 'sonner';
import { Users, Mail, DollarSign, Calendar, Loader2, FileText, Search } from 'lucide-react';
import { motion } from 'framer-motion';

const MerchantCustomersTab = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const res = await getMerchantCustomers();
      setCustomers(res.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const viewCustomerDetails = async (email) => {
    try {
      const res = await getMerchantCustomer(email);
      setCustomerDetails(res.data);
      setNoteText(res.data.notes || '');
      setSelectedCustomer(email);
    } catch (error) {
      toast.error('Failed to load customer details');
    }
  };

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await addCustomerNote(selectedCustomer, noteText);
      toast.success('Note saved');
    } catch (error) {
      toast.error('Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-6" data-testid="customers-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-heading font-semibold">Customers</h2>
        <div className="relative w-64">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-xl"
            data-testid="customer-search"
          />
        </div>
      </div>

      {filteredCustomers.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No customers yet</p>
            <p className="text-sm text-muted-foreground">Customers will appear here after their first payment</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredCustomers.map((customer, index) => (
            <motion.div
              key={customer.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="hover:border-emerald-500/50 transition-colors cursor-pointer"
                    onClick={() => viewCustomerDetails(customer.email)}
                    data-testid={`customer-${customer.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                        <span className="font-semibold text-emerald-600">
                          {(customer.name || customer.email)?.[0]?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{customer.name || 'Anonymous'}</p>
                        <p className="text-sm text-muted-foreground">{customer.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-emerald-600">${customer.total_spent.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">
                        {customer.total_transactions} transaction{customer.total_transactions !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Customer Detail Dialog */}
      <Dialog open={!!selectedCustomer} onOpenChange={() => setSelectedCustomer(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Customer Details</DialogTitle>
          </DialogHeader>
          {customerDetails && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <span className="font-bold text-2xl text-emerald-600">
                    {(customerDetails.name || customerDetails.email)?.[0]?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-lg">{customerDetails.name || 'Anonymous'}</p>
                  <p className="text-muted-foreground">{customerDetails.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-xl bg-muted">
                  <p className="text-xs text-muted-foreground">Total Spent</p>
                  <p className="font-bold text-lg">${customerDetails.total_spent.toFixed(2)}</p>
                </div>
                <div className="p-3 rounded-xl bg-muted">
                  <p className="text-xs text-muted-foreground">Transactions</p>
                  <p className="font-bold text-lg">{customerDetails.total_transactions}</p>
                </div>
                <div className="p-3 rounded-xl bg-muted">
                  <p className="text-xs text-muted-foreground">First Payment</p>
                  <p className="font-medium">{formatDate(customerDetails.first_payment_at)}</p>
                </div>
                <div className="p-3 rounded-xl bg-muted">
                  <p className="text-xs text-muted-foreground">Last Payment</p>
                  <p className="font-medium">{formatDate(customerDetails.last_payment_at)}</p>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Notes</p>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  className="w-full p-3 rounded-xl border border-input bg-background text-sm resize-none"
                  rows={3}
                  placeholder="Add notes about this customer..."
                  data-testid="customer-notes"
                />
                <Button onClick={saveNote} disabled={savingNote} size="sm" className="rounded-lg">
                  {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Note'}
                </Button>
              </div>

              {customerDetails.transactions?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Recent Transactions</p>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {customerDetails.transactions.slice(0, 5).map((tx) => (
                      <div key={tx.id} className="flex justify-between text-sm p-2 rounded-lg bg-muted">
                        <span>${tx.amount.toFixed(2)} {tx.currency}</span>
                        <span className="text-muted-foreground">{formatDate(tx.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MerchantCustomersTab;
